import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-02 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/ai_health_tracker_coach/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so the food/weight log stays
// on-device (context sends meal NAMES only — never notes). The regex intent-router below
// is the FREE FALLBACK (offline / signed-out / out of funds) — chat never goes dead.
// The MEDICAL fence (the fleet's strictest) still holds: question-shaped medical messages
// are refused PRE-LLM (free, no billed call at all), and a routed "ask" action is fenced
// again before the billed /ask endpoint. Original prototype notes:
//
// It maps ONE user message → ONE ChatAction,
// reusing ONLY what THIS app already has (no new/edited server routes, no deploy):
//   • "log/add meal X, 450 calories"      → local parse                 → add_meal   (store.createMeal)
//   • "log weight 72.5"                   → local parse                 → add_weight (store.upsertWeight)
//   • "recap my week"                     → EXISTING /recap             → recap      (billed)
//   • "ask: which day …" / question-y     → EXISTING /ask               → ask        (billed, medical-fenced)
//   • "rename/set X calories to N"        → resolve id locally          → edit_meal  (store.updateMeal)
//   • "delete/remove X"                   → resolve id locally          → delete_meal (UI confirms)
//   • "show/find/list … meals"            → local filter over listMeals → filter
//   • anything else                       → none + a short clarifying nudge
//
// It's a small deterministic keyword router — no billed call except recap/ask, which reuse
// the app's live endpoints. The medical fence (brain.medicalRefusal) is honored before any
// billed call, mirroring the main screen.

import { store, todayLocal, parseNum, type Meal, type MealPatch, type MealSource } from "../store";
import { BrainError, medicalRefusal, brainCall } from "../brain";
import type { ChatAction, MealFilter, FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; executeAction supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

const HELP =
  "I can log meals and weight, edit or delete entries, search your log, and describe your week — just tell me. Try:\n" +
  '• "log meal chicken salad, 450 calories"\n' +
  '• "log weight 72.5"\n' +
  '• "recap my week"\n' +
  '• "show meals from today" · "set chicken salad calories to 500"\n' +
  '• "delete chicken salad"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// Forgiving meal lookup: an exact name still wins (keeps "salad" unambiguous when an exact "salad"
// exists), otherwise the FLEET fuzzy resolver (see ./fuzzy) tolerates misspellings, dropped words
// and word order. Meals carry `name`, so no adapter is needed; resolution stays client-side.
async function findByName(term: string): Promise<Meal[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listMeals({ limit: 50000 });
  const exact = all.filter((m) => m.name.trim().toLowerCase() === q);
  return exact.length ? exact : findNamedMatches(all, term);
}

/** "Couldn't find X" enriched with the closest meal names ("did you mean …?"). */
async function notFoundFor(term: string): Promise<string> {
  const all = await store.listMeals({ limit: 50000 });
  return notFoundMessage(all, term);
}

function pickList(rows: Meal[]): string {
  return rows.slice(0, 5).map((m) => `• ${m.name} (${m.eaten_on}, ~${m.calories} kcal est.)`).join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  // MEDICAL fence, PRE-LLM and FREE: a question-shaped message that trips the fence is
  // refused before ANY billed call (mirrors the local router's ask branch — the shape gate
  // keeps "log meal keto salad" loggable). toAction re-fences the routed "ask" as backstop.
  if (/\?\s*$/.test(text) || /^(how|which|when|what|did|have|should|am|is|are|can|do|does|why)\b/i.test(text)) {
    const refusal = medicalRefusal(text);
    if (refusal) { const r = none(refusal); return { reply: r.reply, actions: [r.action] }; }
  }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (meal name → row id) locally,
// because the log lives here. Context = meal NAMES only (metadata) — notes and
// per-meal details never leave the device through the chat route.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.health.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? `${cur}\n- ${line}` : `- ${line}`); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_meal", description: "Log one meal the user ate.", params: { name: "meal name — INFER from the user's words", calories: "optional integer kcal ESTIMATE — if the user doesn't know, use estimate_meal instead of asking them", protein_g: "optional grams of protein (number)", carbs_g: "optional grams of carbs (number)", fat_g: "optional grams of fat (number)", eaten_on: "optional YYYY-MM-DD — resolve 'yesterday' etc. from CURRENT DATE" } },
  { name: "estimate_meal", description: "ESTIMATE the calories/macros of a described meal (the AI does the guessing, the user confirms). Use when the user describes food without numbers or asks 'how many calories is X'.", params: { description: "the meal in the user's words, e.g. 'chicken rice and a coke'" } },
  { name: "add_weight", description: "Log the user's body weight for today (re-logging a day overwrites it).", params: { weight_kg: "body weight in KILOGRAMS — CONVERT yourself if the user speaks pounds/stone ('160 lbs' → 72.6); never ask for kg" } },
  { name: "delete_weight", description: "Delete a logged weight entry by its date.", params: { date: "YYYY-MM-DD of the entry (resolve 'today'/'yesterday')" } },
  { name: "edit_meal", description: "Change one already-logged meal; identify it by its meal name from APP DATA.", params: { name: "which meal (its name)", set_name: "optional new name", set_calories: "optional corrected integer kcal estimate", set_protein_g: "optional grams of protein (number)", set_carbs_g: "optional grams of carbs (number)", set_fat_g: "optional grams of fat (number)", set_eaten_on: "optional new date YYYY-MM-DD", set_note: "optional replacement note" } },
  { name: "delete_meal", description: "Delete one logged meal identified by its meal name (the app asks the user to confirm first).", params: { name: "which meal (its name)" } },
  { name: "filter", description: "Search / filter the user's meal log.", params: { text: "optional text the meal name contains", date: "optional exact day YYYY-MM-DD (resolve words like today/yesterday to a date)", source: "optional how it was logged: manual | photo | label | barcode" } },
  { name: "recap", description: "Describe the user's recent logging — the observational weekly recap over their own log (no params)." },
  { name: "ask", description: "Answer a question about the user's own logged data (their totals, streaks, patterns). NEVER for medical/diet advice.", params: { question: "the user's question, verbatim" } },
  { name: "backup_save", description: "Back up the WHOLE food+weight log to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore the log from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's whole log to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use when the user wants to store their data locally right now / 'save my data to a file'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup they brought from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file', 'bring in my data from a file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE food+weight log instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave (e.g. 'my usual breakfast is oatmeal, 350 kcal', 'I log weight in pounds').", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  // SMALL context: recent distinct meal names (newest first, capped) so the brain can
  // resolve references like "delete the chicken salad". Names only — never notes.
  const meals = await store.listMeals({ limit: 500 });
  const names: string[] = [];
  for (const m of meals) {
    const n = m.name.trim();
    if (n && !names.some((x) => x.toLowerCase() === n.toLowerCase())) names.push(n);
    if (names.length >= 40) break;
  }
  const notes = getInstructions();
  const context = `LIBRARY (their saved plans/notes): ${librarySummary()}. Meal names in the user's log (newest first): ${names.join(", ") || "(none yet)"}.` +
    (notes ? `\nCustomer's standing instructions (apply when filling params): ${notes}` : "");
  // 20 turns (the server's cap) — 8 made the brain forget a pasted list after ~4 rounds.
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, onChunk);
  // The shared brain returns an ARRAY (G17): "log chicken salad and log my weight 72" = two actions.
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, text);
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
/** Finite number within [min, max] — anything else (incl. non-numeric strings) → undefined. */
const asNum = (v: unknown, min: number, max: number): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) && n >= min && n <= max ? n : undefined;
};
const asKcal = (v: unknown): number | undefined => {
  const n = asNum(v, 0, 20000);
  return n === undefined ? undefined : Math.round(n);
};
const asGrams = (v: unknown): number | undefined => asNum(v, 0, 5000);
const asKg = (v: unknown): number | undefined => {
  const n = asNum(v, 0, 700); // same ceiling the local router enforces
  return n !== undefined && n > 0 ? n : undefined;
};
const asDate = (v: unknown): string | undefined => {
  const s = asStr(v);
  return s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : undefined;
};
const SOURCES = new Set<MealSource>(["manual", "photo", "label", "barcode"]);
const asSource = (v: unknown): MealSource | undefined => {
  const s = asStr(v)?.toLowerCase() as MealSource | undefined;
  return s && SOURCES.has(s) ? s : undefined;
};

// Resolve a meal-name reference to exactly one local row, or explain why not.
async function resolveOne(term: string | undefined): Promise<{ row?: Meal; problem?: string }> {
  if (!term) return { problem: "Which meal do you mean? Give me its name." };
  const rows = await findByName(term);
  if (rows.length === 0) return { problem: await notFoundFor(term) };
  if (rows.length > 1) return { problem: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` };
  return { row: rows[0] };
}

export async function toAction(r: ServerChat, text: string): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_meal": {
      const name = asStr(p.name);
      if (!name) return none(say || 'What should the meal be called? Try "log meal chicken salad, 450 calories".');
      return { reply: say, action: { kind: "add_meal", name, calories: asKcal(p.calories), protein_g: asGrams(p.protein_g), eaten_on: asDate(p.eaten_on) } };
    }
    case "add_weight": {
      const kg = asKg(p.weight_kg);
      if (kg === undefined) return none(say || 'What weight should I log? Try "log weight 72.5".');
      return { reply: say, action: { kind: "add_weight", weight_kg: kg } };
    }
    case "edit_meal": {
      const { row, problem } = await resolveOne(asStr(p.name));
      if (!row) return none(problem!);
      const patch: MealPatch = {};
      const newName = asStr(p.set_name);
      if (newName) patch.name = newName;
      const kcal = asKcal(p.set_calories);
      if (kcal !== undefined) patch.calories = kcal;
      const prot = asGrams(p.set_protein_g);
      if (prot !== undefined) patch.protein_g = prot;
      const carbs = asGrams(p.set_carbs_g);
      if (carbs !== undefined) patch.carbs_g = carbs;
      const fat = asGrams(p.set_fat_g);
      if (fat !== undefined) patch.fat_g = fat;
      const day = asDate(p.set_eaten_on);
      if (day) patch.eaten_on = day;
      const note = asStr(p.set_note);
      if (note !== undefined) patch.note = note;
      if (Object.keys(patch).length === 0) return none(say || "What should I change on it — name, calories, protein/carbs/fat, date, or note?");
      return { reply: say, action: { kind: "edit_meal", id: row.id, patch, label: `"${row.name}"` } };
    }
    case "delete_meal": {
      const { row, problem } = await resolveOne(asStr(p.name));
      if (!row) return none(problem!);
      return { reply: say, action: { kind: "delete_meal", id: row.id, label: `"${row.name}" (${row.eaten_on})` } };
    }
    case "filter": {
      // An empty filter is valid here — it lists everything, same as the local router.
      const filter: MealFilter = {};
      const term = asStr(p.text);
      if (term) filter.text = term;
      const day = asDate(p.date);
      if (day) filter.date = day;
      const src = asSource(p.source);
      if (src) filter.source = src;
      return { reply: "", action: { kind: "filter", filter, label: say || undefined } };
    }
    case "recap":
      return { reply: say, action: { kind: "recap" } };
    case "estimate_meal": {
      const description = asStr(p.description);
      if (!description) return none(say || "Describe the meal and I'll estimate it.");
      return { reply: say, action: { kind: "estimate_meal", description } };
    }
    case "delete_weight": {
      const date = asStr(p.date);
      if (!date) return none(say || "Which day's weight should I delete?");
      return { reply: say, action: { kind: "delete_weight", date } };
    }
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    case "ask": {
      const question = asStr(p.question) ?? text;
      // MEDICAL fence backstop — blocks the billed /ask (askHealth re-fences too).
      const refusal = medicalRefusal(question) ?? medicalRefusal(text);
      if (refusal) return none(refusal);
      return { reply: say, action: { kind: "ask", question } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 1) LOG WEIGHT — local parse (no server call).
  const wM = t.match(/^(?:log|add|record|set)\s+(?:my\s+)?weight[:\s]+([\d.,]+)\s*(?:kg)?\s*$/i);
  if (wM) {
    const v = parseNum(wM[1]);
    if (v === null || v <= 0 || v > 700) return none('What weight should I log? Try "log weight 72.5".');
    return { reply: "", action: { kind: "add_weight", weight_kg: v } };
  }

  // 2) LOG MEAL — local parse. Name + optional "N calories" + optional macros.
  const addM = t.match(/^(?:log|add|create|new|save)\s+(?:a\s+)?(?:meal|food|lunch|dinner|breakfast|snack)[:\-\s]+([\s\S]+)$/i)
    || t.match(/^(?:i\s+(?:ate|had))\s+([\s\S]+)$/i);
  if (addM) {
    let rest = addM[1].trim();
    let calories: number | undefined;
    const calM = rest.match(/[,;]?\s*(?:about|around|~)?\s*([\d.,]+)\s*(?:k?cal(?:orie)?s?)\b/i);
    if (calM && typeof calM.index === "number") {
      const v = parseNum(calM[1]);
      if (v !== null) calories = Math.round(v);
      rest = (rest.slice(0, calM.index) + rest.slice(calM.index + calM[0].length)).trim();
    }
    let protein_g: number | undefined;
    const pM = rest.match(/[,;]?\s*([\d.,]+)\s*g?\s*protein\b/i);
    if (pM && typeof pM.index === "number") {
      const v = parseNum(pM[1]);
      if (v !== null) protein_g = v;
      rest = (rest.slice(0, pM.index) + rest.slice(pM.index + pM[0].length)).trim();
    }
    const name = rest.replace(/[,\s]+$/, "").trim();
    if (!name) return none('What should the meal be called? Try "log meal chicken salad, 450 calories".');
    return { reply: "", action: { kind: "add_meal", name, calories, protein_g } };
  }

  // 3) RECAP — the billed weekly recap (EXISTING /recap endpoint).
  if (/\b(recap|summar(y|ize|ise)|describe)\b.*\b(week|log|month|days?)\b/i.test(low) || /^recap\b/i.test(low)) {
    return { reply: "", action: { kind: "recap" } };
  }

  // 4) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|trash)\s+(?:the\s+)?(?:meal\s+)?(?:for\s+|called\s+)?([\s\S]+)$/i);
  if (delM) {
    const term = delM[1].trim();
    const rows = await findByName(term);
    if (rows.length === 0) return none(await notFoundFor(term));
    if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
    return { reply: "", action: { kind: "delete_meal", id: rows[0].id, label: `"${rows[0].name}" (${rows[0].eaten_on})` } };
  }

  // 5) EDIT — updateMeal exists in the store, so the chat can edit. Single-match resolution only.
  const edit = await routeEdit(t);
  if (edit) return edit;

  // 6) FILTER / SEARCH — query-shaped → local filter over the on-device log.
  if ((/\b(show|find|list|search|which|filter|what)\b/i.test(low) && /\bmeals?\b/i.test(low))
      || /\bwhat did i eat\b/i.test(low)
      || /^(show|find|list|search|filter)\b/i.test(low)) {
    const filter = parseFilter(t);
    return { reply: "", action: { kind: "filter", filter, label: t } };
  }

  // 7) A question about the log → the billed /ask endpoint (medical-fenced pre-call).
  if (/\?\s*$/.test(t) || /^(how|which|when|what|did|have)\b/i.test(low)) {
    const refusal = medicalRefusal(t);
    if (refusal) return none(refusal);
    return { reply: "", action: { kind: "ask", question: t } };
  }

  // 8) Unclear → a short clarifying nudge.
  return none(HELP);
}

// EDIT intents. Returns null if the message isn't an edit so the router can fall through.
async function routeEdit(t: string): Promise<One | null> {
  // "rename X to Y"
  let m = t.match(/^rename\s+(.+?)\s+to\s+(.+)$/i);
  if (m) {
    const name = m[2].trim().replace(/[.?!]+$/, "");
    if (!name) return null;
    return resolveEdit(m[1], { name }, `renamed to "${name}"`);
  }

  // "set/change X('s) calories to N"
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+calories\s+to\s+([\d.,]+)\s*$/i);
  if (m) {
    const v = parseNum(m[2]);
    if (v === null) return none("Calories should be a number (kcal, an estimate).");
    return resolveEdit(m[1], { calories: Math.round(v) }, `calories → ~${Math.round(v)} kcal est.`);
  }

  // "set/change X('s) protein|carbs|fat to N"
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+(protein|carbs?|fat)\s+to\s+([\d.,]+)\s*g?\s*$/i);
  if (m) {
    const v = parseNum(m[3]);
    if (v === null) return none("That should be a number of grams.");
    const field = m[2].toLowerCase().startsWith("protein") ? "protein_g" : m[2].toLowerCase().startsWith("carb") ? "carbs_g" : "fat_g";
    const patch: MealPatch = { [field]: v } as MealPatch;
    return resolveEdit(m[1], patch, `${m[2].toLowerCase()} → ${v}g`);
  }

  // "move X to <date>" — YYYY-MM-DD only (keep it deterministic).
  m = t.match(/^(?:move|set|change)\s+(.+?)(?:'s date)?\s+to\s+(\d{4}-\d{2}-\d{2})\s*$/i);
  if (m) return resolveEdit(m[1], { eaten_on: m[2] }, `date → ${m[2]}`);

  return null;
}

async function resolveEdit(term: string, patch: MealPatch, note?: string): Promise<One> {
  const rows = await findByName(term);
  if (rows.length === 0) return none(await notFoundFor(term));
  if (rows.length > 1) return none(`I found ${rows.length} matching "${term.trim()}" — be more specific:\n${pickList(rows)}`);
  const r = rows[0];
  const label = note ? `"${r.name}" (${note})` : `"${r.name}"`;
  return { reply: "", action: { kind: "edit_meal", id: r.id, patch, label } };
}

/** Build a local MealFilter from free text — date words, source, text term. */
function parseFilter(text: string): MealFilter {
  const low = text.toLowerCase();
  const f: MealFilter = {};
  if (/\btoday\b/i.test(low)) f.date = todayLocal();
  else if (/\byesterday\b/i.test(low)) {
    const d = new Date(); d.setDate(d.getDate() - 1);
    f.date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  const dM = text.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (dM) f.date = dM[1];
  if (/\bphoto\b/i.test(low)) f.source = "photo";
  else if (/\blabel\b/i.test(low)) f.source = "label";
  else if (/\bbarcode\b/i.test(low)) f.source = "barcode";
  const withM = text.match(/\b(?:with|containing|matching|called|named)\s+([a-z][\w -]*)/i);
  if (withM) f.text = withM[1].trim();
  return f;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
